import { useCallback, useEffect, useRef, useState } from 'react';
import usePolling from '../utils/usePolling';

/**
 * Poll a loader and keep the last good result.
 *
 *   const { data, error, loading, loadedAt, reload } = usePlatformData(
 *     () => loadDatabase(range), [range]);
 *
 * Same contract as the Dashboard's fetch path: a failure after a success keeps
 * the old data on screen AND sets `error`, so the page can say how old the
 * figures are instead of silently showing stale numbers. `deps` changing (a new
 * range, another service) clears the data, because the old series would be
 * drawn against the new axis.
 */
export default function usePlatformData(loader, deps, { intervalMs = 60000 } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: true, loadedAt: null });
  const latest = useRef(loader);
  latest.current = loader;
  const seq = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const data = await latest.current();
      if (mine !== seq.current) return;
      setState({ data, error: null, loading: false, loadedAt: new Date() });
    } catch (e) {
      if (mine !== seq.current) return;
      setState(prev => ({ ...prev, error: e?.message || 'the request failed', loading: false }));
    }
  }, []);

  // Declared before the poll so a `deps` change clears the old series before the reload.
  useEffect(() => {
    setState({ data: null, error: null, loading: true, loadedAt: null });
    if (!intervalMs) reload();  // not polled: one load per `deps` change
    // `deps` is the caller's dependency list, forwarded on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reload, intervalMs]);
  // Only while the tab is visible (utils/usePolling).
  usePolling(reload, intervalMs, deps);

  return { ...state, reload };
}
